import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  FormControl,
  Alert
} from '@mui/material';
import { CircularProgress } from '@mui/material';
import {
  Person as PersonIcon,
  LocationOn as LocationIcon,
  ShoppingBag as OrdersIcon,
  LocalOffer as CouponIcon,
  Star as ReviewIcon,
  Favorite as WishlistIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Google as GoogleIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Password Management Component
const PasswordManagement = ({ user }) => {
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const { createPassword, changePassword, hasPasswordProvider, hasGoogleProvider, linkWithGoogle } = useAuth();

  const handlePasswordChange = (field) => (event) => {
    setPasswordForm(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleCreatePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await createPassword(passwordForm.newPassword);
      toast.success('Password created successfully!');
      setShowPasswordForm(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Password updated successfully!');
      setShowPasswordForm(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkGoogle = async () => {
    setLoading(true);
    try {
      await linkWithGoogle();
      toast.success('Google account linked successfully!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const hasPassword = hasPasswordProvider();
  const hasGoogle = hasGoogleProvider();

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Security & Authentication
      </Typography>
      
      {/* Account Providers */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Connected Accounts:
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          {hasPassword && (
            <Chip 
              label="Email & Password" 
              color="primary" 
              variant="outlined"
              icon={<EmailIcon />}
            />
          )}
          {hasGoogle && (
            <Chip 
              label="Google" 
              color="success" 
              variant="outlined"
              icon={<GoogleIcon />}
            />
          )}
        </Box>
      </Box>

      {/* Password Management */}
      <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
        {hasPassword ? (
          <Button
            variant="outlined"
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            disabled={loading}
          >
            Change Password
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            disabled={loading}
          >
            Create Password
          </Button>
        )}
        
        {!hasGoogle && (
          <Button
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={handleLinkGoogle}
            disabled={loading}
            sx={{
              borderColor: '#dadce0',
              color: '#3c4043',
              '&:hover': {
                borderColor: '#dadce0',
                backgroundColor: '#f8f9fa'
              }
            }}
          >
            Link Google Account
          </Button>
        )}
      </Box>

      {/* Password Form */}
      {showPasswordForm && (
        <Paper sx={{ p: 3, mt: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle1" gutterBottom>
            {hasPassword ? 'Change Password' : 'Create Password'}
          </Typography>
          
          <Grid container spacing={2}>
            {hasPassword && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current Password"
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange('currentPassword')}
                  required
                />
              </Grid>
            )}
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange('newPassword')}
                helperText="At least 6 characters"
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange('confirmPassword')}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  onClick={hasPassword ? handleChangePassword : handleCreatePassword}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    hasPassword ? 'Update Password' : 'Create Password'
                  )}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

const ProfileTab = ({ user, userData, onUpdateProfile, sendVerificationEmailNow, refreshEmailVerificationStatus, sendingVerification }) => {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    displayName: userData?.displayName || '',
    email: user?.email || '',
    phoneNumber: userData?.phoneNumber || ''
  });

  const handleChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    try {
      await onUpdateProfile(formData);
      setEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Avatar
            sx={{ width: 120, height: 120, mx: 'auto', mb: 2, fontSize: '2rem' }}
          >
            {userData?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
          </Avatar>
          <Typography variant="h6">{userData?.displayName || 'User'}</Typography>
          <Box display="flex" flexDirection="column" alignItems="center" mt={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
              {user && user.emailVerified && (
                <CheckCircleIcon color="success" fontSize="small" titleAccess="Email verified" />
              )}
            </Box>
            {user && !user.emailVerified && (
              <Box mt={1} display="flex" gap={1}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<EmailIcon />}
                  disabled={sendingVerification}
                  onClick={async ()=>{
                    try { await sendVerificationEmailNow(); toast.success('Verification email sent'); }
                    catch(e){ toast.error('Failed to send email'); }
                  }}
                >
                  {sendingVerification? 'Sending...' : 'Verify Email'}
                </Button>
                <IconButton 
                  size="small" 
                  onClick={refreshEmailVerificationStatus} 
                  title="Refresh status"
                  aria-label="Refresh email verification status"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            sx={{ mt: 2 }}
            onClick={() => setEditing(!editing)}
          >
            {editing ? 'Cancel' : 'Edit Profile'}
          </Button>
        </Paper>
      </Grid>

      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Personal Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.displayName}
                onChange={handleChange('displayName')}
                disabled={!editing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={formData.email}
                disabled
                helperText="Email cannot be changed"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={handleChange('phoneNumber')}
                disabled={!editing}
              />
            </Grid>
          </Grid>

          {/* Password Management Section */}
          <Divider sx={{ my: 3 }} />
          <PasswordManagement user={user} />
          
          {editing && (
            <Box mt={2}>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{ mr: 2 }}
              >
                Save Changes
              </Button>
              <Button
                variant="outlined"
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

// Address Modal Component
const AddressModal = ({ open, onClose, onSave, editingAddress = null }) => {
  const [formData, setFormData] = useState({
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    phoneNumber: '',
    type: 'Home',
    customType: ''
  });

  React.useEffect(() => {
    if (editingAddress) {
      setFormData(editingAddress);
    } else {
      setFormData({
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        pincode: '',
        phoneNumber: '',
        type: 'Home',
        customType: ''
      });
    }
  }, [editingAddress, open]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Validation
    if (!formData.addressLine1.trim()) {
      toast.error('Address Line 1 is required');
      return;
    }
    if (!formData.state.trim()) {
      toast.error('State is required');
      return;
    }
    if (!formData.city.trim()) {
      toast.error('City is required');
      return;
    }
    if (!formData.pincode.trim() || !/^\d{6}$/.test(formData.pincode)) {
      toast.error('Valid 6-digit pincode is required');
      return;
    }
    if (!formData.phoneNumber.trim() || !/^\d{10}$/.test(formData.phoneNumber)) {
      toast.error('Valid 10-digit phone number is required');
      return;
    }
    if (formData.type === 'Other' && !formData.customType.trim()) {
      toast.error('Custom address type is required');
      return;
    }

    const addressData = {
      ...formData,
      fullAddress: `${formData.addressLine1}${formData.addressLine2 ? ', ' + formData.addressLine2 : ''}, ${formData.city}, ${formData.state}, ${formData.pincode}`,
      displayType: formData.type === 'Other' ? formData.customType : formData.type
    };

    onSave(addressData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editingAddress ? 'Edit Address' : 'Add New Address'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 1*"
              value={formData.addressLine1}
              onChange={handleChange('addressLine1')}
              placeholder="House/Flat/Office No, Building Name, Street"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 2"
              value={formData.addressLine2}
              onChange={handleChange('addressLine2')}
              placeholder="Area, Colony, Sector, Landmark"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="State*"
              value={formData.state}
              onChange={handleChange('state')}
              placeholder="Enter state name"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="City*"
              value={formData.city}
              onChange={handleChange('city')}
              placeholder="Enter city name"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Pincode*"
              value={formData.pincode}
              onChange={handleChange('pincode')}
              placeholder="000000"
              inputProps={{ maxLength: 6 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number*"
              value={formData.phoneNumber}
              onChange={handleChange('phoneNumber')}
              placeholder="9876543210"
              inputProps={{ maxLength: 10 }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Address Type*</FormLabel>
              <RadioGroup
                value={formData.type}
                onChange={handleChange('type')}
                row
              >
                <FormControlLabel value="Home" control={<Radio />} label="Home" />
                <FormControlLabel value="Office" control={<Radio />} label="Office" />
                <FormControlLabel value="Other" control={<Radio />} label="Other" />
              </RadioGroup>
            </FormControl>
          </Grid>

          {formData.type === 'Other' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Custom Address Type*"
                value={formData.customType}
                onChange={handleChange('customType')}
                placeholder="e.g., Friend's House, Relative's Place"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          {editingAddress ? 'Update' : 'Save'} Address
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Addresses Tab Component
const AddressesTab = () => {
  const { user, userData } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load addresses from Firestore when component mounts
  useEffect(() => {
    loadAddresses();
  }, [user]);

  const loadAddresses = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const userDoc = await getDoc(doc(firestore, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setAddresses(userData.addresses || []);
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
      toast.error('Failed to load addresses');
    } finally {
      setLoading(false);
    }
  };

  const saveAddressesToFirestore = async (updatedAddresses) => {
    try {
      const userRef = doc(firestore, 'users', user.uid);
      await updateDoc(userRef, {
        addresses: updatedAddresses,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error saving addresses:', error);
      toast.error('Failed to save address');
      throw error;
    }
  };

  const handleAddAddress = async (addressData) => {
    try {
      let updatedAddresses;
      
      if (editingAddress) {
        // Update existing address
        updatedAddresses = addresses.map((addr, index) => 
          index === editingAddress.index ? { ...addressData, id: addr.id } : addr
        );
        toast.success('Address updated successfully!');
      } else {
        // Add new address
        const newAddress = {
          ...addressData,
          id: Date.now() // Simple ID generation
        };
        updatedAddresses = [...addresses, newAddress];
        toast.success('Address added successfully!');
      }

      // Save to Firestore
      await saveAddressesToFirestore(updatedAddresses);
      
      // Update local state
      setAddresses(updatedAddresses);
      setEditingAddress(null);
    } catch (error) {
      console.error('Error handling address:', error);
      // Don't show error toast here since saveAddressesToFirestore already shows it
    }
  };

  const handleEditAddress = (address, index) => {
    setEditingAddress({ ...address, index });
    setShowAddressModal(true);
  };

  const handleDeleteAddress = async (index) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      try {
        const updatedAddresses = addresses.filter((_, i) => i !== index);
        
        // Save to Firestore
        await saveAddressesToFirestore(updatedAddresses);
        
        // Update local state
        setAddresses(updatedAddresses);
        toast.success('Address deleted successfully!');
      } catch (error) {
        console.error('Error deleting address:', error);
        // Error toast already shown by saveAddressesToFirestore
      }
    }
  };

  const handleCloseModal = () => {
    setShowAddressModal(false);
    setEditingAddress(null);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Manage Addresses</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddressModal(true)}
        >
          Add New Address
        </Button>
      </Box>
      
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <Typography>Loading addresses...</Typography>
        </Box>
      ) : addresses.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LocationIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Addresses Added
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Add your addresses for faster checkout
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddressModal(true)}
          >
            Add Your First Address
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {addresses.map((address, index) => (
            <Grid item xs={12} md={6} key={address.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Chip 
                      label={address.displayType} 
                      color="primary" 
                      size="small" 
                    />
                    <Box>
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditAddress(address, index)}
                        sx={{ mr: 1 }}
                        aria-label={`Edit ${address.displayType} address`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteAddress(index)}
                        color="error"
                        aria-label={`Delete ${address.displayType} address`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {address.fullAddress}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Phone: {address.phoneNumber}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <AddressModal
        open={showAddressModal}
        onClose={handleCloseModal}
        onSave={handleAddAddress}
        editingAddress={editingAddress}
      />
    </Box>
  );
};

// Orders Tab Component
const OrdersTab = () => {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentUser) loadOrders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const { orderService } = require('../services/api');
      const response = await orderService.getOrders();
      const ordersData = response.data.orders || response.data || [];
      const valid = Array.isArray(ordersData) ? ordersData.filter(o=>o && o.id) : [];
      setOrders(valid);
    } catch (err) {
      console.error('MyAccount OrdersTab: failed to load orders', err);
      setOrders([]);
    } finally { setLoading(false); }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>My Orders</Typography>
      {loading && (
        <Box sx={{ py:4, textAlign:'center' }}>
          <CircularProgress size={32} />
        </Box>
      )}
      {!loading && orders.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <OrdersIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>No Orders Yet</Typography>
          <Typography variant="body2" color="text.secondary">Your order history will appear here</Typography>
        </Paper>
      )}
      {!loading && orders.length > 0 && (
        <Grid container spacing={2}>
          {orders.map(order => (
            <Grid item xs={12} key={order.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight:600 }}>#{order.orderNumber || order.id}</Typography>
                    <Chip
                      size="small"
                      label={
                        order.status === 'inProduction'
                          ? 'In Production'
                          : (order.status || 'pending').toUpperCase()
                      }
                      color={
                        order.status === 'delivered'
                          ? 'success'
                          : order.status === 'cancelled'
                            ? 'error'
                            : order.status === 'inProduction'
                              ? 'info'
                              : 'warning'
                      }
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb:1 }}>
                    {Array.isArray(order.items)? `${order.items.length} item(s)` : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight:500 }}>Total: ₹{order.pricing?.total || order.totalAmount || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

// Coupons Tab Component
const CouponsTab = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAvailableCoupons();
  }, []);

  const fetchAvailableCoupons = async () => {
    try {
      setLoading(true);
      setError('');
      const { couponService } = require('../services/api');
      const response = await couponService.getAvailableCoupons();
      setCoupons(response.data.coupons || []);
    } catch (err) {
      console.error('Error fetching available coupons:', err);
      setError(err.response?.data?.error || 'Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDiscountText = (coupon) => {
    if (coupon.type === 'percentage') {
      return `${coupon.value}% OFF`;
    } else if (coupon.type === 'fixed') {
      return `₹${coupon.value} OFF`;
    }
    return `${coupon.value} OFF`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`Coupon code "${text}" copied to clipboard!`);
    }).catch(() => {
      toast.error('Failed to copy coupon code');
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Available Coupons</Typography>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={fetchAvailableCoupons}
          size="small"
        >
          Refresh
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {coupons.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CouponIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Coupons Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check back later for exciting offers!
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {coupons.map((coupon) => (
            <Grid item xs={12} md={6} key={coupon.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  position: 'relative',
                  '&:hover': {
                    boxShadow: 3,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Discount Badge */}
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      bgcolor: 'primary.main',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      fontWeight: 'bold',
                      fontSize: '0.875rem'
                    }}
                  >
                    {getDiscountText(coupon)}
                  </Box>

                  {/* Coupon Code */}
                  <Box display="flex" alignItems="center" mb={2} mt={1}>
                    <Typography 
                      variant="h5" 
                      fontWeight="bold" 
                      sx={{ 
                        fontFamily: 'monospace',
                        letterSpacing: 1,
                        color: 'primary.main'
                      }}
                    >
                      {coupon.code}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => copyToClipboard(coupon.code)}
                      sx={{ ml: 2, minWidth: 'auto', px: 1 }}
                    >
                      Copy
                    </Button>
                  </Box>

                  {/* Description */}
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {coupon.description}
                  </Typography>

                  {/* Coupon Details */}
                  <Box>
                    {coupon.minOrderValue > 0 && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        • Minimum order value: ₹{coupon.minOrderValue}
                      </Typography>
                    )}
                    
                    {coupon.maxDiscountAmount && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        • Maximum discount: ₹{coupon.maxDiscountAmount}
                      </Typography>
                    )}
                    
                    {coupon.applicationType !== 'all' && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        • Applies to: {coupon.applicationType === 'specific' ? 'Selected products' : coupon.category}
                      </Typography>
                    )}
                    
                    {coupon.endDate && (
                      <Typography variant="caption" display="block" color="error.main" mt={1}>
                        • Valid until: {formatDate(coupon.endDate)}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

// Reviews Tab Component
const ReviewsTab = () => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(()=>{ if(user) loadReviews(true); }, [user]);

  const loadReviews = async (initial=false) => {
    if(!user) return; 
    try {
      initial ? setLoading(true) : setLoadingMore(true);
      const { productService } = require('../services/api');
      const params = {};
      if(!initial && nextCursor) params.cursor = nextCursor;
      const res = await productService.getMyReviews(params);
      const newReviews = res.data.reviews||[];
      setReviews(prev => initial ? newReviews : [...prev, ...newReviews]);
      setNextCursor(res.data.nextCursor || null);
    } catch(e){ console.error('Load my reviews error', e); }
    finally { initial ? setLoading(false) : setLoadingMore(false); }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>My Reviews & Ratings</Typography>
      {loading && (
        <Box sx={{ py:4, textAlign:'center' }}><CircularProgress size={32} /></Box>
      )}
      {!loading && reviews.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ReviewIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Reviews Yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Share your experience by reviewing products you've purchased
          </Typography>
        </Paper>
      )}
      {!loading && reviews.length > 0 && (
        <Box>
          <Grid container spacing={2}>
            {reviews.map((review) => (
              <Grid item xs={12} key={review.productId + review.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="subtitle1" sx={{ fontWeight:600 }}>{review.productName}</Typography>
                      <Chip size="small" label={`${review.rating}★`} color="primary" />
                    </Box>
                    {review.title && <Typography variant="subtitle2" sx={{ mt:1 }}>{review.title}</Typography>}
                    {review.body && <Typography variant="body2" sx={{ mt:0.5 }} color="text.secondary">{review.body}</Typography>}
                    <Typography variant="caption" color="text.secondary" sx={{ display:'block', mt:1 }}>
                      {review.createdAt?.toDate ? review.createdAt.toDate().toLocaleDateString() : ''}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {nextCursor && (
            <Box sx={{ textAlign:'center', mt:3 }}>
              <Button variant="outlined" onClick={()=>loadReviews(false)} disabled={loadingMore}>
                {loadingMore? 'Loading...' : 'Load More'}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

// Wishlist Tab Component
const WishlistTab = () => {
  const { user } = useAuth();
  const [wishlist, setWishlist] = useState([]); // list of product objects
  const [loading, setLoading] = useState(false);

  useEffect(()=>{ if(user) loadWishlist(); }, [user]);

  const loadWishlist = async () => {
    try {
      setLoading(true);
      const { productService } = require('../services/api');
      const wlRes = await productService.getMyWishlist();
      const ids = wlRes.data.wishlist || [];
      if(ids.length === 0){ setWishlist([]); return; }
      // Fetch each product (could optimize with batch endpoint later)
      const prodPromises = ids.map(id => productService.getProduct(id).then(r=> ({ id, data:r.data }) ).catch(()=>null));
      const results = await Promise.all(prodPromises);
      const products = results.filter(Boolean).map(r=> ({ ...r.data, docId: r.id, name: (r.data && (r.data.name||r.data.id)) }));
      setWishlist(products);
    } catch(e){ console.error('Load wishlist error', e); setWishlist([]); }
    finally { setLoading(false); }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>My Wishlist</Typography>
      {loading && (<Box sx={{ py:4, textAlign:'center' }}><CircularProgress size={32} /></Box>)}
      {!loading && wishlist.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <WishlistIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Your Wishlist is Empty
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Save products you love to your wishlist
          </Typography>
        </Paper>
      )}
      {!loading && wishlist.length > 0 && (
        <Grid container spacing={2}>
          {wishlist.map((item) => (
            <Grid item xs={12} md={6} key={item.docId || item.id}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight:600 }}>{item.name}</Typography>
                  {item.price && <Typography variant="body2" color="text.secondary">₹{item.price}</Typography>}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

// Main Component
const MyAccount = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const { user, userData, updateUserProfile, sendVerificationEmailNow, refreshEmailVerificationStatus, sendingVerification } = useAuth();

  const tabs = [
    { label: 'Profile', icon: <PersonIcon /> },
    { label: 'Addresses', icon: <LocationIcon /> },
    { label: 'Orders', icon: <OrdersIcon /> },
    { label: 'Coupons', icon: <CouponIcon /> },
    { label: 'Reviews', icon: <ReviewIcon /> },
    { label: 'Wishlist', icon: <WishlistIcon /> }
  ];

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        My Account
      </Typography>
      
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                label={tab.label}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          <TabPanel value={currentTab} index={0}>
            <ProfileTab 
              user={user} 
              userData={userData} 
              onUpdateProfile={updateUserProfile}
              sendVerificationEmailNow={sendVerificationEmailNow}
              refreshEmailVerificationStatus={refreshEmailVerificationStatus}
              sendingVerification={sendingVerification}
            />
          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            <AddressesTab />
          </TabPanel>
          <TabPanel value={currentTab} index={2}>
            <OrdersTab />
          </TabPanel>
          <TabPanel value={currentTab} index={3}>
            <CouponsTab />
          </TabPanel>
          <TabPanel value={currentTab} index={4}>
            <ReviewsTab />
          </TabPanel>
          <TabPanel value={currentTab} index={5}>
            <WishlistTab />
          </TabPanel>
        </Box>
      </Paper>
    </Container>
  );
};

export default MyAccount;
